import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as React from 'react';

import type {
  GrilleViewDto,
  RateCollectionApiError,
  RateSeniorityLevelDto,
} from '@/api/rate-collection-client';
import {
  useAcquireLock,
  useAutosaveCell,
  useGrille,
  useHeartbeatLock,
  useReleaseLock,
  useReopenSubmission,
  useSubmitSubmission,
} from '@/api/use-rate-collection';
import { useSupplierConditions } from '@/api/use-supplier-conditions';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  profileDisplayLabel as toProfileLabel,
  seniorityLabel,
  submissionStateLabel,
} from '@/lib/rate-collection-labels';
import { SENIORITY_LEVELS_ORDERED } from '@/lib/rate-collection-labels';
import { POSITIONING_ORANGE_PRESENTATION } from '@/lib/positioning-labels';
import { usePositioningActivation } from '@/api/use-positioning';
import { cn } from '@/lib/utils';
import {
  buildControlSummaries,
  cellProgressCount,
  computeCycle1Diff,
  distinctFamilies,
  familyAggregate,
  familyDisplayLabel,
  filterRowsByFamily,
  formatLastSaved,
  missingCells,
  rowAggregate,
  buildRows,
  UNFAMILIED,
  type CellValueResolver,
  type SaisieRow,
} from '@/routes/supplier/rate-collection/saisie-model';
import {
  findActiveCycle,
  findBriefingForCampaign,
} from '@/routes/supplier/rate-collection/saisie-header-model';
import { ControlSummary } from '@/routes/supplier/rate-collection/control-summary';
import { ClonePanel } from '@/routes/supplier/rate-collection/clone-panel';
import { PositioningCell } from '@/routes/supplier/rate-collection/positioning-cell';
import { ProfileDetailDialog } from '@/routes/supplier/rate-collection/profile-detail-dialog';
import { SkillsPanel } from '@/routes/supplier/rate-collection/skills-panel';
import { TjmCellEditor } from '@/routes/supplier/rate-collection/tjm-cell-editor';
import {
  useEditLock,
  type SaisieEditLock,
} from '@/routes/supplier/rate-collection/use-edit-lock';
import {
  useSaisieAutosave,
  type CellEditState,
  type CellSeed,
  type SaisieAutosave,
} from '@/routes/supplier/rate-collection/use-saisie-autosave';
import { useGridKeyboardNav } from '@/routes/supplier/rate-collection/use-grid-keyboard-nav';

// =============================================================================
// tjm-grid-panel.tsx — Grille de saisie TJM EXTRAITE en composant RÉUTILISABLE (PHASE C, maquette #85).
// =============================================================================
// GATE_HUMAN 2026-09-12 (PHASE C) : la grille de saisie profil × niveau, sa colonne latérale
// (avancement / synthèse) et TOUTE la logique de saisie (autosave, submit, EditLock concurrent,
// positioning F-10, rappel Cycle 1) sont EXTRAITES ici pour être embarquées à la fois dans la route
// `/saisie` (écran dédié, deep-link préservé) ET dans le détail `/campagnes` (sous le périmètre métier).
// AUCUNE PERTE DE FONCTIONNALITÉ : autosave (blur + intervalle 2 min + « Sauvegardé … »), soumission,
// ré-ouverture (« Modifier mes tarifs »), verrou d'édition concurrent, validation inline, indicateur de
// positionnement marché (Cycle 2), comparaison Cycle 1 / Cycle 2, cellules « Non concerné », navigation
// clavier + a11y, persistance du brouillon — tout est PRÉSERVÉ, seulement RÉ-AGENCÉ (voir rapport PHASE C).
//
// RÉSILIENCE (ST-SAISIE-02) : ZÉRO donnée métier en localStorage. État = mémoire applicative (React) +
// BASE. Récupération après coupure = RE-FETCH serveur (useGrille) du Brouillon persisté.
//
// UI-ONLY : aucune modification API/contrat/back. Les mêmes hooks/read-models sont réutilisés.

/**
 * Liseré d'état de cellule (DS) dérivé de l'état DÉJÀ connu de l'autosave (aucune nouvelle validation).
 * La couleur est un RENFORT : elle double l'état ARIA/texte porté par TjmCellEditor + la mention « à saisir ».
 *   • invalide / erreur → liseré destructive ;
 *   • renseignée (valeur sauvegardée) → liseré success ;
 *   • vide / non concernée → neutre (transparent, largeur stable).
 */
function cellStateBorderClass(state: CellEditState, notTargeted: boolean): string {
  // GATE_HUMAN 2026-09-12 (maquette #90) : plus de « parenthèse colorée » sur les cellules renseignées —
  // seule l'erreur/invalidité garde un liseré rouge (point 14). Le reste est neutre (cellule propre).
  if (notTargeted) {
    return 'border-transparent';
  }
  if (state.status === 'invalid' || state.status === 'error') {
    return 'border-destructive/60';
  }
  return 'border-transparent';
}

/** Date FR ABRÉGÉE (« 30 sept. 2026 ») — sous-ligne d'échéance (maquette #85 point 2). */
function formatDeadlineShort(iso: string | null): string {
  if (iso === null) {
    return 'non planifiée';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return 'non planifiée';
  }
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Variante d'intégration du panneau (maquette #85). */
export type TjmGridPanelVariant = 'route' | 'embedded';

export interface TjmGridPanelProps {
  readonly submissionId: string;
  /** Famille sélectionnée (état d'URL en route `/saisie` ; état interne en embed `/campagnes`). */
  readonly selectedFamily?: string | undefined;
  /**
   * Notifie d'un changement de filtre famille. En route `/saisie` l'adaptateur écrit l'état d'URL ;
   * en embed `/campagnes` il n'est pas fourni ⇒ le panneau gère le filtre en MÉMOIRE (jamais localStorage).
   */
  readonly onSelectFamily?: (family: string | undefined) => void;
  /** F-10 (S17) : campagne en CYCLE 2 (active la LECTURE de l'indicateur de positionnement). */
  readonly cycle2?: boolean | undefined;
  /** Numéro de cycle brut (search param `cycle`) — alimente la sous-ligne + le header contextuel. */
  readonly cycleNumber?: number | undefined;
  /** R15-CMP-03 : profil deep-linké (1re cellule éditable focalisée à l'ouverture). */
  readonly deepLinkProfile?: string | undefined;
  /** 'route' (/saisie) : titre h1 + sous-ligne campagne. 'embedded' (/campagnes) : titre h3 introductif. */
  readonly variant?: TjmGridPanelVariant;
  /** id du titre du panneau (le parent peut y attacher son `aria-labelledby`). */
  readonly headingId?: string;
  /** Instant de référence (indicateur « Sauvegardé … »), injectable pour le test. */
  readonly now?: Date;
}

const columnHelper = createColumnHelper<SaisieRow>();

interface SaisieTableMeta {
  readonly autosave: SaisieAutosave;
  readonly editLock: SaisieEditLock;
  readonly locked: boolean;
  readonly positioningActive: boolean;
  readonly campaignId: string | undefined;
  readonly cycleNumber: number;
  /** Rappel Cycle 1 + écart C1↔C2 par cellule (Cycle 2 + interrupteur « Comparer au cycle 1 » ON). */
  readonly showCycle1: boolean;
  /**
   * G19 (relevé Gate Human 2026-09-15) : campagne en Cycle 2 (l'interrupteur « Comparer au cycle 1 »
   * existe). Sert à RÉSERVER en permanence l'espace de la 2e ligne (rappel Cycle 1) pour que la hauteur
   * de ligne NE CHANGE PAS quand la comparaison bascule ON/OFF. Indépendant de `showCycle1`.
   */
  readonly cycle2: boolean;
  readonly onOpenProfile: (profileCode: string) => void;
}

export function TjmGridPanel({
  submissionId,
  selectedFamily,
  onSelectFamily,
  cycle2 = false,
  cycleNumber,
  deepLinkProfile,
  variant = 'route',
  headingId = 'tjm-grid-heading',
  now,
}: TjmGridPanelProps): React.JSX.Element {
  const grilleQuery = useGrille(submissionId);
  const autosaveMutation = useAutosaveCell(submissionId);
  const submitMutation = useSubmitSubmission(submissionId);
  const reopenMutation = useReopenSubmission(submissionId);
  const acquireLockMutation = useAcquireLock(submissionId);
  const releaseLockMutation = useReleaseLock(submissionId);
  const heartbeatLockMutation = useHeartbeatLock(submissionId);
  const conditions = useSupplierConditions();

  const view: GrilleViewDto | undefined = grilleQuery.data;

  // Filtre famille CONTRÔLÉ (route `/saisie`, état d'URL) ou INTERNE (embed `/campagnes`, mémoire React).
  const [internalFamily, setInternalFamily] = React.useState<string | undefined>(undefined);
  const controlledFamily = onSelectFamily !== undefined;
  const activeFamily = controlledFamily ? selectedFamily : internalFamily;
  const handleSelectFamily = React.useCallback(
    (family: string | undefined): void => {
      if (onSelectFamily !== undefined) {
        onSelectFamily(family);
      } else {
        setInternalFamily(family);
      }
    },
    [onSelectFamily],
  );

  // Interrupteur « Comparer au cycle 1 » (maquette #85 point 12) — masque la 2e ligne des cellules (saisie
  // pure). Actif uniquement en Cycle 2 ; par défaut ON (la comparaison est visible d'emblée). État de vue
  // en mémoire (jamais localStorage).
  const [compareCycle1, setCompareCycle1] = React.useState(true);

  const [submissionState, setSubmissionState] = React.useState(
    view?.submissionState ?? 'Vide',
  );
  React.useEffect(() => {
    if (view !== undefined) {
      setSubmissionState(view.submissionState);
    }
  }, [view]);

  const [deadlineLocked, setDeadlineLocked] = React.useState(false);
  const [detailProfileCode, setDetailProfileCode] = React.useState<string | null>(null);
  const openProfileDetail = React.useCallback((profileCode: string) => {
    setDetailProfileCode(profileCode);
  }, []);
  const closeProfileDetail = React.useCallback(() => {
    setDetailProfileCode(null);
  }, []);
  React.useEffect(() => {
    const err = submitMutation.error as RateCollectionApiError | null;
    if (err !== null && err.kind === 'deadline-passed') {
      setDeadlineLocked(true);
    }
  }, [submitMutation.error]);

  const isSubmitted = submissionState === 'Soumis';
  const isNegotiationLocked = submissionState === 'Verrouille';
  const submittedOrLocked = isSubmitted || isNegotiationLocked;
  const locked = submittedOrLocked || deadlineLocked;

  const [justReopened, setJustReopened] = React.useState(false);
  const reopenError = reopenMutation.error as RateCollectionApiError | null;
  const onReopen = React.useCallback((): void => {
    reopenMutation.mutate(undefined, {
      onSuccess: (ack) => {
        setSubmissionState(ack.submissionState);
        setJustReopened(true);
      },
      onError: (error) => {
        if ((error as RateCollectionApiError).kind === 'deadline-passed') {
          setDeadlineLocked(true);
        }
      },
    });
  }, [reopenMutation]);

  const negotiationReopened = submissionState === 'ReModifiable';

  const rows: readonly SaisieRow[] = React.useMemo(
    () => (view !== undefined ? buildRows(view) : []),
    [view],
  );

  const seeds: readonly CellSeed[] = React.useMemo(() => {
    if (view === undefined) {
      return [];
    }
    return view.cells.map((c) => ({
      profileCode: c.profileCode,
      seniorityLevel: c.seniorityLevel,
      savedValue: c.tjmValueEur,
      lastSavedAt: c.lastSavedAt,
    }));
  }, [view]);

  const onSavedAck = React.useCallback((state: string): void => {
    setSubmissionState(state as GrilleViewDto['submissionState']);
  }, []);

  const onSaveError = React.useCallback((error: RateCollectionApiError): void => {
    if (error.kind === 'deadline-passed') {
      setDeadlineLocked(true);
    }
  }, []);

  const autosave: SaisieAutosave = useSaisieAutosave({
    seeds,
    locked,
    mutateAsync: autosaveMutation.mutateAsync,
    onSavedAck,
    onSaveError,
  });

  const editLock: SaisieEditLock = useEditLock({
    acquire: acquireLockMutation.mutateAsync,
    release: releaseLockMutation.mutateAsync,
    heartbeat: heartbeatLockMutation.mutateAsync,
    disabled: locked,
  });

  const campaignId = view?.campaignId;
  const POSITIONING_CYCLE_NUMBER = 2;

  const activationQuery = usePositioningActivation(
    campaignId,
    POSITIONING_CYCLE_NUMBER,
    cycle2,
  );
  const positioningActive =
    cycle2 && (activationQuery.data?.indicators.length ?? 0) > 0;
  const marketReadingActive =
    cycle2 && (activationQuery.data?.indicators.some((i) => !i.masked) ?? false);
  const showCycle1 = cycle2 && compareCycle1;

  const gridRef = useGridKeyboardNav();

  const visibleRows = React.useMemo(
    () => filterRowsByFamily(rows, activeFamily),
    [rows, activeFamily],
  );

  // Deep-link cellule (R15-CMP-03) — inchangé.
  const deepLinkFocusedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (deepLinkProfile === undefined || !grilleQuery.isSuccess) {
      return;
    }
    if (deepLinkFocusedRef.current === deepLinkProfile) {
      return;
    }
    const safe = deepLinkProfile.replace(/[^a-zA-Z0-9_-]/g, '_');
    const input = document.querySelector<HTMLInputElement>(
      `input[id^="tjm-${safe}-"]:not([disabled])`,
    );
    if (input !== null) {
      deepLinkFocusedRef.current = deepLinkProfile;
      input.scrollIntoView({ block: 'center' });
      input.focus();
    }
  }, [deepLinkProfile, grilleQuery.isSuccess]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor((row) => toProfileLabel(row.profileCode, row.profileLabel), {
        id: 'profile',
        header: 'Profil',
        cell: (ctx) => {
          const meta = ctx.table.options.meta as SaisieTableMeta;
          const profileCode = ctx.row.original.profileCode;
          return (
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => meta.onOpenProfile(profileCode)}
                aria-haspopup="dialog"
                className={cn(
                  'text-left font-medium text-foreground underline-offset-2 hover:underline',
                  'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                {ctx.getValue()}
                <span className="sr-only"> — ouvrir la fiche profil</span>
              </button>
              <span className="font-mono text-xs text-muted-foreground">
                {profileCode}
              </span>
            </div>
          );
        },
      }),
      ...SENIORITY_LEVELS_ORDERED.map((level: RateSeniorityLevelDto, levelIndex: number) =>
        columnHelper.display({
          id: `level-${level}`,
          // Maquette #85 point 9 : en-têtes de niveau ALIGNÉS À DROITE, séparés par des filets verticaux.
          header: () => <span className="block text-right">{seniorityLabel(level)}</span>,
          cell: (ctx) => {
            const meta = ctx.table.options.meta as SaisieTableMeta;
            const row = ctx.row.original;
            const cellState = meta.autosave.getCell(row.profileCode, level);
            const lockState = meta.editLock.getLock(row.profileCode, level);
            const profileDisplay = toProfileLabel(row.profileCode, row.profileLabel);
            const notTargeted = row.cells[level].targeted === false;
            const cycle1Value = row.cells[level].cycle1ValueEur;
            const invalid =
              cellState.status === 'invalid' || cellState.status === 'error';
            // Maquette #85 point 14 : cellule VIDE (aucune valeur sauvegardée, brouillon vide, non invalide,
            // couple concerné) ⇒ fond ivoire pâle + mention « à saisir » (ambre foncé, portée par la cellule).
            const isEmpty =
              !notTargeted &&
              !invalid &&
              cellState.savedValue === null &&
              cellState.draft.trim() === '';
            return (
              <div
                className={cn(
                  'flex flex-col gap-1 rounded-md border-l-2 pl-2 pr-1',
                  cellStateBorderClass(cellState, notTargeted),
                  isEmpty && 'bg-chart-3/5',
                )}
              >
                <TjmCellEditor
                  profileCode={row.profileCode}
                  profileDisplayLabel={profileDisplay}
                  seniorityLevel={level}
                  rowIndex={ctx.row.index}
                  colIndex={levelIndex}
                  state={cellState}
                  disabled={meta.locked || notTargeted}
                  notTargeted={notTargeted}
                  lockedByOther={lockState.lockedByOther}
                  holderContactId={lockState.holderContactId}
                  showEuroAdornment={!notTargeted}
                  {...(isEmpty ? { placeholder: '—' } : {})}
                  onFocus={() => meta.editLock.onFocusCell(row.profileCode, level)}
                  onDraftChange={(draft) =>
                    meta.autosave.setDraft(row.profileCode, level, draft)
                  }
                  onBlur={() => {
                    meta.editLock.onBlurCell(row.profileCode, level);
                    meta.autosave.flushCell(row.profileCode, level);
                  }}
                  onRetry={() => meta.autosave.retryCell(row.profileCode, level)}
                />
                {/* Cellule vide : mention d'état « à saisir » (ambre foncé). GATE_HUMAN 2026-09-12 : le
                    « cible € » de la maquette n'était qu'une indication ; aucun champ « cible » n'existe et
                    on NE fabrique PAS de valeur cible. La référence Cycle 1 reste portée par la ligne de
                    comparaison (TjmCellEditor) ci-dessus, logique pré-existante inchangée. */}
                {isEmpty && (
                  <span className="text-xs font-medium text-amber-700">à saisir</span>
                )}
                {/* F-10 (S17) : indicateur de marché — s'affiche UNIQUEMENT quand il existe (point 11 :
                    « Indicateur indisponible » supprimé partout ; un couple masqué ne rend rien). */}
                {meta.positioningActive && (
                  <PositioningCell
                    campaignId={meta.campaignId}
                    cycleNumber={meta.cycleNumber}
                    profileCode={row.profileCode}
                    seniorityLevel={level}
                    savedValue={cellState.savedValue}
                    active={meta.positioningActive}
                  />
                )}
                {/* Maquette #85 point 10/12 : 2e ligne = rappel Cycle 1 + écart en %. Masquée par
                    l'interrupteur « Comparer au cycle 1 » (saisie pure).
                    G19 (relevé Gate Human 2026-09-15) : en Cycle 2, l'espace de cette 2e ligne est
                    RÉSERVÉ en permanence (slot de hauteur fixe) afin que la hauteur de ligne NE CHANGE
                    PAS quand la comparaison bascule ON/OFF. Le rappel n'est rendu que si `showCycle1`,
                    mais le slot reste présent (vide si OFF) — hauteur stable dans les deux états. */}
                {meta.cycle2 && (
                  <div data-testid="cycle1-slot" className="min-h-[1.25rem]">
                    {meta.showCycle1 && (
                      <Cycle1Recall
                        cycle1ValueEur={cycle1Value}
                        currentValueEur={cellState.savedValue}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          },
        }),
      ),
      // G16 (relevé Gate Human 2026-09-15) : à l'extrémité de CHAQUE ligne (profil), deux cellules
      // d'agrégat — Moyenne et Médiane des taux SAISIS du profil (cellules vides exclues, helper
      // `rowAggregate` réutilisant l'agrégat maison). Le sens est porté par l'EN-TÊTE de colonne
      // (libellé texte « Moyenne » / « Médiane », scope=col) doublé de l'en-tête de ligne (profil) —
      // jamais une couleur. `—` si le profil n'a encore aucun taux saisi.
      columnHelper.display({
        id: 'row-mean',
        header: () => <span className="block text-right">Moyenne</span>,
        cell: (ctx) => {
          const meta = ctx.table.options.meta as SaisieTableMeta;
          const agg = rowAggregate(
            ctx.row.original,
            (p, l) => meta.autosave.getCell(p, l).savedValue,
          );
          return <RowAggregateValue value={agg.meanEur} />;
        },
      }),
      columnHelper.display({
        id: 'row-median',
        header: () => <span className="block text-right">Médiane</span>,
        cell: (ctx) => {
          const meta = ctx.table.options.meta as SaisieTableMeta;
          const agg = rowAggregate(
            ctx.row.original,
            (p, l) => meta.autosave.getCell(p, l).savedValue,
          );
          return <RowAggregateValue value={agg.medianEur} />;
        },
      }),
    ],
    [],
  );

  const table = useReactTable<SaisieRow>({
    data: visibleRows as SaisieRow[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      autosave,
      editLock,
      locked,
      positioningActive,
      campaignId,
      cycleNumber: POSITIONING_CYCLE_NUMBER,
      showCycle1,
      cycle2,
      onOpenProfile: openProfileDetail,
    } satisfies SaisieTableMeta,
  });

  const submitError = submitMutation.error as RateCollectionApiError | null;
  const stateLabel = submissionStateLabel(submissionState);

  const resolveCellValue: CellValueResolver = React.useCallback(
    (profileCode, level) => autosave.getCell(profileCode, level).savedValue,
    [autosave],
  );

  const controlSummaries = React.useMemo(
    () => buildControlSummaries(rows, resolveCellValue),
    [rows, resolveCellValue],
  );

  // Maquette #85 point 16 : avancement au grain TAUX (X / Y taux) sur la vue COURANTE (famille filtrée),
  // UN SEUL compteur EXACT. Le détail liste les couples restant à saisir.
  const cellProgress = React.useMemo(
    () => cellProgressCount(visibleRows, resolveCellValue),
    [visibleRows, resolveCellValue],
  );
  const cellsToFill = React.useMemo(
    () => missingCells(visibleRows, resolveCellValue),
    [visibleRows, resolveCellValue],
  );

  // Maquette #85 point 17 : synthèse (Moyenne + Médiane, PAS de Somme) sur la vue COURANTE.
  const synthesisAggregate = React.useMemo(
    () => familyAggregate(visibleRows, resolveCellValue),
    [visibleRows, resolveCellValue],
  );
  const synthesisFamilyLabel =
    activeFamily === undefined
      ? 'Toutes familles'
      : familyDisplayLabel(activeFamily);

  // Chips de famille (point 7) : « Toutes les familles » + chaque famille + compteur de profils (point 8).
  const familyChips = React.useMemo(() => {
    const families = distinctFamilies(rows);
    return families.map((key) => {
      const familyRows =
        key === UNFAMILIED
          ? rows.filter((r) => r.family === null)
          : rows.filter((r) => r.family === key);
      return { key, label: familyDisplayLabel(key), count: familyRows.length };
    });
  }, [rows]);

  const [controlOpen, setControlOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const confirmSubmit = React.useCallback((): void => {
    setConfirmOpen(false);
    submitMutation.mutate();
  }, [submitMutation]);

  // Sous-ligne contextuelle (point 2) : « <intitulé> · cycle N · échéance <date> » — dérivée du briefing
  // fournisseur déjà exposé (GET /supplier/conditions), corrélé par campaignId. Aucun endpoint nouveau.
  const briefing = findBriefingForCampaign(conditions.data, campaignId);
  const activeCycle =
    briefing !== null ? findActiveCycle(briefing.cycles, cycleNumber) : null;
  const cycleForSubline = activeCycle?.cycleNumber ?? cycleNumber;
  const subline =
    briefing !== null
      ? [
          briefing.intitule,
          cycleForSubline !== undefined ? `cycle ${String(cycleForSubline)}` : null,
          activeCycle !== null
            ? `échéance ${formatDeadlineShort(activeCycle.closeAt)}`
            : null,
        ]
          .filter((s): s is string => s !== null)
          .join(' · ')
      : null;

  const nowMs = (now ?? new Date()).getTime();

  // Header CTA (points 4/5) : uniquement la RÉ-OUVERTURE. Soumis ⇒ « Modifier mes tarifs » (ré-ouvre).
  // G18 (relevé Gate Human 2026-09-15) : le bouton « Soumettre / Re-soumettre la grille » n'est PLUS
  // dans l'en-tête — il est regroupé avec « Sauvegarder » et « Contrôler / Masquer le contrôle » dans
  // la barre d'actions (footer). Verrouille & deadline ⇒ aucun CTA d'en-tête.
  const reopenCta =
    !deadlineLocked && !isNegotiationLocked && view !== undefined && isSubmitted ? (
      <Button type="button" onClick={onReopen} disabled={reopenMutation.isPending}>
        {reopenMutation.isPending ? 'Réouverture…' : 'Modifier mes tarifs'}
      </Button>
    ) : null;

  // G18 : bouton de soumission REGROUPÉ dans la barre d'actions (footer), aux côtés de « Sauvegarder »
  // et « Masquer le contrôle ». Toute la logique (deadlineLocked, états Soumis/Verrouillé, disabled,
  // libellé Re-soumettre en négociation) est PRÉSERVÉE ; seul l'emplacement change. La barre n'est
  // rendue que lorsque la grille n'est pas verrouillée (`!locked`), condition identique à l'ancien CTA.
  const submitButton = (
    <Button
      type="button"
      disabled={locked || submitMutation.isPending || autosave.isSaving}
      onClick={() => setConfirmOpen(true)}
    >
      {submitMutation.isPending
        ? negotiationReopened
          ? 'Re-soumission…'
          : 'Soumission…'
        : negotiationReopened
          ? 'Re-soumettre la grille'
          : 'Soumettre la grille'}
    </Button>
  );

  const HeadingTag = variant === 'route' ? 'h1' : 'h3';

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête refondu (points 1-5) : titre + badge NOIR collé + sous-ligne à gauche ; « Sauvegardé … »
          gris + UNIQUE CTA ambre à droite. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <HeadingTag
              id={headingId}
              className={cn(
                'font-semibold tracking-tight text-foreground',
                variant === 'route' ? 'text-2xl' : 'text-lg',
              )}
            >
              Saisie des taux journaliers
            </HeadingTag>
            {/* Point 1 : badge NOIR compact collé au titre (état de la grille). */}
            <span className="inline-flex items-center rounded-md bg-foreground px-2 py-0.5 text-xs font-medium text-background">
              <span className="sr-only">État de la grille&nbsp;: </span>
              {stateLabel.label}
            </span>
          </div>
          {/* Point 2 : sous-ligne (route uniquement ; en embed le contexte campagne est déjà présent). */}
          {variant === 'route' && subline !== null && (
            <p className="text-sm text-muted-foreground">{subline}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SaveIndicator autosave={autosave} nowMs={nowMs} />
          {reopenCta}
        </div>
      </header>

      {grilleQuery.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement de la grille…
        </p>
      )}

      {grilleQuery.isError && (
        <Card>
          <CardContent className="p-4">
            <p role="alert" className="text-sm text-destructive">
              Impossible de charger la grille. {grilleQuery.error.message}
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-3"
              onClick={() => void grilleQuery.refetch()}
            >
              Réessayer
            </Button>
          </CardContent>
        </Card>
      )}

      {view !== undefined && (
        <>
          {!locked && <ClonePanel submissionId={submissionId} disabled={locked} />}

          {deadlineLocked && (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted px-4 py-2 text-sm text-foreground"
            >
              <span>
                La date limite de soumission est dépassée&nbsp;: la grille est en lecture seule. Si un
                administrateur a prolongé le cycle, réessayez pour reprendre votre saisie.
              </span>
              {/* BUG-FIX 2026-09-14 : récupération SANS rechargement après une prolongation admin. Lève le
                  verrou local et re-synchronise la grille sur l'état serveur (deadline live). Si la deadline
                  est réellement encore dépassée, la prochaine sauvegarde/soumission re-verrouille (409). */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDeadlineLocked(false);
                  void grilleQuery.refetch();
                }}
              >
                Réessayer
              </Button>
            </div>
          )}

          {/* Point 4 : le bandeau « soumise / verrouillée » DISPARAÎT (le CTA « Modifier mes tarifs » est
              remonté dans l'en-tête). On conserve UNIQUEMENT l'erreur de ré-ouverture non ré-ouvrable. */}
          {isSubmitted && !deadlineLocked && reopenError !== null &&
            reopenError.kind === 'not-reopenable' && (
              <p role="alert" className="text-sm text-destructive">
                {reopenError.message}
              </p>
            )}

          {isNegotiationLocked && !deadlineLocked && (
            <p
              role="status"
              className="rounded-md border border-border bg-muted px-4 py-2 text-sm text-foreground"
            >
              La grille est verrouillée&nbsp;: la saisie est close (aucune modification possible).
            </p>
          )}

          {justReopened && submissionState === 'Brouillon' && !deadlineLocked && (
            <p
              role="status"
              className="rounded-md border border-border bg-muted px-4 py-2 text-sm text-foreground"
            >
              Vous modifiez des tarifs déjà soumis&nbsp;: ajustez la grille puis re-soumettez. La
              re-soumission remplacera votre soumission précédente.
            </p>
          )}

          {negotiationReopened && !deadlineLocked && (
            <p
              role="status"
              className="rounded-md border border-border bg-muted px-4 py-2 text-sm text-foreground"
            >
              Saisie rouverte pour négociation&nbsp;: ajustez vos tarifs puis re-soumettez. Toute
              re-soumission remplace l’intégralité de votre version précédente.
            </p>
          )}

          {/* Maquette #85 points 6-18 : tableau fluide à gauche + colonne latérale 300px STICKY à droite. */}
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              {/* Tête de tableau (points 7-8, 12) : chips de famille + interrupteur « Comparer au cycle 1 »
                  + légende de positionnement (Cycle 2). */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <FamilyChips
                  chips={familyChips}
                  total={rows.length}
                  activeFamily={activeFamily}
                  onSelectFamily={handleSelectFamily}
                />
                <div className="flex flex-wrap items-center gap-3">
                  {marketReadingActive && <PositioningLegend />}
                  {cycle2 && (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={compareCycle1}
                      onClick={() => setCompareCycle1((v) => !v)}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        compareCycle1
                          ? 'border-primary bg-accent text-accent-foreground'
                          : 'border-border text-foreground hover:bg-muted',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'inline-block h-2 w-2 rounded-full',
                          compareCycle1 ? 'bg-primary' : 'bg-muted-foreground/40',
                        )}
                      />
                      Comparer au cycle 1
                    </button>
                  )}
                </div>
              </div>

              {marketReadingActive && (
                <p className="text-sm text-muted-foreground">
                  Cycle 2&nbsp;: une cellule est signalée «&nbsp;Au-dessus du marché&nbsp;» quand
                  votre tarif dépasse le haut de la fourchette du marché anonymisé. Le signal est
                  doublé d’un libellé&nbsp;; aucune borne, aucun tarif individuel ni fournisseur
                  tiers n’est exposé.
                </p>
              )}

              <Card>
                <CardContent className="p-0">
                  <table
                    ref={gridRef}
                    className="ds-table w-full border-collapse text-sm"
                  >
                    <caption className="sr-only">
                      Grille de saisie des taux journaliers par profil et niveau de séniorité.
                      Utilisez les flèches du clavier pour vous déplacer entre les cellules.
                    </caption>
                    <thead>
                      {table.getHeaderGroups().map((hg) => (
                        <tr key={hg.id} className="border-b border-border">
                          {hg.headers.map((header, headerIndex) => (
                            <th
                              key={header.id}
                              scope="col"
                              className={cn(
                                'px-4 py-3 font-semibold text-foreground',
                                headerIndex === 0
                                  ? 'text-left'
                                  : 'border-l border-border text-right',
                              )}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-border last:border-0 hover:bg-muted/40"
                        >
                          {row.getVisibleCells().map((cell, index) =>
                            index === 0 ? (
                              <th
                                key={cell.id}
                                scope="row"
                                className="px-4 py-3 text-left align-top font-normal"
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </th>
                            ) : (
                              <td
                                key={cell.id}
                                className="border-l border-border px-3 py-3 align-top"
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ),
                          )}
                        </tr>
                      ))}
                      {visibleRows.length === 0 && (
                        <tr>
                          <td
                            // Profil + 4 niveaux + 2 agrégats de ligne (Moyenne, Médiane, G16).
                            colSpan={SENIORITY_LEVELS_ORDERED.length + 3}
                            className="px-4 py-6 text-center text-muted-foreground"
                          >
                            Aucun profil pour cette famille.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* Point 15 : la légende sous le tableau est SUPPRIMÉE (l'état est porté par les cellules). */}
                </CardContent>
              </Card>

              {/* SF1 « Sauvegarder » manuel + SF2 « Contrôler » + G18 « Soumettre la grille » REGROUPÉS
                  dans une même barre d'actions (masqués quand la grille est verrouillée). */}
              <footer className="flex flex-col items-start gap-3">
                {!locked && (
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={autosave.isSaving}
                      onClick={() => autosave.flushAll()}
                    >
                      Sauvegarder
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      aria-expanded={controlOpen}
                      onClick={() => setControlOpen((open) => !open)}
                    >
                      {controlOpen ? 'Masquer le contrôle' : 'Contrôler'}
                    </Button>
                    {/* G18 : bouton de soumission (CTA primaire) aux côtés de Sauvegarder / Contrôler. */}
                    {submitButton}
                  </div>
                )}
                {submitError !== null && (
                  <p role="alert" className="text-sm text-destructive">
                    {submitError.kind === 'already-submitted'
                      ? 'Cette grille a déjà été soumise. La saisie est close.'
                      : submitError.kind === 'deadline-passed'
                        ? 'La date limite de soumission est dépassée : la grille est en lecture seule.'
                        : submitError.message}
                  </p>
                )}
                {submitMutation.isSuccess && (
                  <p role="status" className="text-sm text-foreground">
                    Grille soumise. Vos taux ont été transmis ; un e-mail de confirmation vous a été
                    envoyé.
                  </p>
                )}
                {autosave.hasError && (
                  <p className="text-xs text-destructive">
                    Certaines cellules n’ont pas pu être enregistrées. Corrigez-les avant de soumettre.
                  </p>
                )}
              </footer>

              {/* SF2 — panneau de CONTRÔLE read-only (récap par famille), ouvert à la demande. */}
              {controlOpen && !locked && (
                <Card>
                  <CardContent className="p-4">
                    <ControlSummary summaries={controlSummaries} />
                  </CardContent>
                </Card>
              )}

              {/* S13 (C) Déclaration de compétences par profil (masqué si verrouillé). */}
              {!locked && <SkillsPanel submissionId={submissionId} disabled={locked} />}
            </div>

            {/* Colonne latérale 300px STICKY (points 16-18). Div simple (PAS un landmark `aside` : la
                <section> parente est déjà une région ; un complémentaire imbriqué violerait WCAG). */}
            <div className="flex w-full shrink-0 flex-col gap-4 lg:sticky lg:top-6 lg:w-[300px]">
              <AdvancementTile progress={cellProgress} missing={cellsToFill} />
              <SynthesisTile
                familyLabel={synthesisFamilyLabel}
                meanEur={synthesisAggregate.meanEur}
                medianEur={synthesisAggregate.medianEur}
              />
            </div>
          </div>

          <ConfirmDialog
            open={confirmOpen}
            title={negotiationReopened ? 'Re-soumettre la grille' : 'Soumettre la grille'}
            description={
              negotiationReopened
                ? 'Cette re-soumission remplace l’intégralité de votre version précédente et clôt votre saisie négociée ; un e-mail de confirmation vous sera envoyé. Confirmez-vous la re-soumission ?'
                : 'Une fois soumise, votre grille de taux ne pourra plus être modifiée et un e-mail de confirmation vous sera envoyé. Confirmez-vous la soumission ?'
            }
            confirmLabel={
              negotiationReopened ? 'Confirmer la re-soumission' : 'Confirmer la soumission'
            }
            confirmDisabled={submitMutation.isPending}
            onConfirm={confirmSubmit}
            onCancel={() => setConfirmOpen(false)}
          >
            <ControlSummary
              summaries={controlSummaries}
              heading="Récapitulatif de vos taux"
            />
          </ConfirmDialog>
        </>
      )}

      <ProfileDetailDialog
        submissionId={submissionId}
        profileCode={detailProfileCode}
        onClose={closeProfileDetail}
      />
    </div>
  );
}

/** Indicateur PERMANENT d'état d'enregistrement (point 3). Texte + couleur (jamais couleur seule). */
function SaveIndicator({
  autosave,
  nowMs,
}: {
  readonly autosave: SaisieAutosave;
  readonly nowMs: number;
}): React.JSX.Element {
  let message: string;
  let tone: string;
  if (autosave.isSaving) {
    message = 'Enregistrement en cours…';
    tone = 'text-muted-foreground';
  } else if (autosave.hasError) {
    message = 'Échec d’enregistrement — la saisie est conservée, réessayez.';
    tone = 'text-destructive';
  } else if (autosave.lastSavedAt !== null) {
    // Point 3 : « Sauvegardé le <date> à HH:MM » (minutes si < 1 h, heure si le jour même, date sinon).
    message = formatLastSaved(autosave.lastSavedAt, nowMs);
    tone = 'text-muted-foreground';
  } else {
    message = 'Vos saisies sont enregistrées automatiquement.';
    tone = 'text-muted-foreground';
  }
  return (
    <p
      role="status"
      aria-live="polite"
      data-testid="save-indicator"
      className={cn('text-sm', tone)}
    >
      {message}
    </p>
  );
}

/** Chips de filtre par famille (point 7) + compteur de profils par chip (point 8). Une seule famille active. */
function FamilyChips({
  chips,
  total,
  activeFamily,
  onSelectFamily,
}: {
  readonly chips: readonly { key: string; label: string; count: number }[];
  readonly total: number;
  readonly activeFamily: string | undefined;
  readonly onSelectFamily: (family: string | undefined) => void;
}): React.JSX.Element {
  const chipClass = (active: boolean): string =>
    cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      active
        ? 'border-primary bg-accent font-medium text-accent-foreground'
        : 'border-border text-foreground hover:bg-muted',
    );
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Familles de profils">
      <button
        type="button"
        aria-pressed={activeFamily === undefined}
        onClick={() => onSelectFamily(undefined)}
        className={chipClass(activeFamily === undefined)}
      >
        Toutes les familles
        <span className="font-mono text-xs text-muted-foreground">{total}</span>
      </button>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          aria-pressed={activeFamily === chip.key}
          onClick={() => onSelectFamily(chip.key)}
          className={chipClass(activeFamily === chip.key)}
        >
          {chip.label}
          <span className="font-mono text-xs text-muted-foreground">{chip.count}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Tuile AVANCEMENT (point 16) : « X / Y taux » + barre ambre + détail des couples restants. UN SEUL
 * compteur EXACT (grain taux). WCAG : la valeur est portée par un LIBELLÉ TEXTE (progressbar en renfort).
 */
function AdvancementTile({
  progress,
  missing,
}: {
  readonly progress: { entered: number; total: number };
  readonly missing: readonly { profileCode: string; profileLabel: string; level: RateSeniorityLevelDto }[];
}): React.JSX.Element {
  const { entered, total } = progress;
  const pct = total === 0 ? 0 : Math.round((entered / total) * 100);
  const label = `${String(entered)} / ${String(total)} taux`;
  const remaining = total - entered;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Avancement
      </p>
      <p className="text-sm font-medium text-foreground">
        {label} ({pct}&nbsp;%)
      </p>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={entered}
        aria-label={`Avancement de la saisie : ${label}`}
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-amber-500"
          style={{ width: `${String(pct)}%` }}
        />
      </div>
      {remaining > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-foreground">
            {String(remaining)} taux à saisir
          </p>
          <ul className="flex flex-col gap-0.5">
            {missing.slice(0, 6).map((m) => (
              <li
                key={`${m.profileCode}-${m.level}`}
                className="text-xs text-muted-foreground"
              >
                {m.profileLabel} · {seniorityLabel(m.level)}
              </li>
            ))}
            {missing.length > 6 && (
              <li className="text-xs text-muted-foreground">
                … et {String(missing.length - 6)} autre{missing.length - 6 > 1 ? 's' : ''}
              </li>
            )}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Tous les taux ciblés sont saisis.</p>
      )}
    </div>
  );
}

/** Formate un montant EUR entier (agrégat confidentiel, arrondi à l'euro). */
function formatEur(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return `${Math.round(value).toLocaleString('fr-FR')} €`;
}

/**
 * G16 — cellule d'agrégat en bout de ligne (Moyenne / Médiane des taux saisis du profil). Rendu texte
 * aligné à droite (mono, tabular-nums), cohérent avec les cellules de taux. L'information est portée par
 * la valeur + l'en-tête de colonne (libellé texte) — jamais une couleur. `—` si aucun taux saisi.
 */
function RowAggregateValue({ value }: { readonly value: number | null }): React.JSX.Element {
  return (
    <div
      className={cn(
        'px-1 text-right font-mono text-[15px] tabular-nums',
        value === null ? 'text-muted-foreground' : 'text-foreground',
      )}
    >
      {formatEur(value)}
    </div>
  );
}

/**
 * Tuile SYNTHÈSE (point 17) : Moyenne + Médiane en liste (PAS de Somme, retiré GATE_HUMAN ; « Taux
 * renseignés » fusionné dans l'Avancement). Note de confidentialité GRISE dessous (point 18).
 */
function SynthesisTile({
  familyLabel,
  meanEur,
  medianEur,
}: {
  readonly familyLabel: string;
  readonly meanEur: number | null;
  readonly medianEur: number | null;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4">
      <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Synthèse · {familyLabel}
      </p>
      <dl className="flex flex-col gap-1.5 text-sm">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Moyenne</dt>
          <dd className="font-mono tabular-nums text-foreground">{formatEur(meanEur)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Médiane</dt>
          <dd className="font-mono tabular-nums text-foreground">{formatEur(medianEur)}</dd>
        </div>
      </dl>
      {/* Point 18 : la bannière verte devient une note GRISE (confidentialité client-side). */}
      <p className="text-xs text-muted-foreground">
        Calculée dans votre navigateur à partir de vos seules saisies ; jamais transmise.
      </p>
    </div>
  );
}

/** Légende F-10 UNAIRE (G0-S17-16) : l'unique état orange, rendu explicite (couleur ↔ libellé ↔ icône). */
function PositioningLegend(): React.JSX.Element {
  const { label, Icon, badgeClassName, description } = POSITIONING_ORANGE_PRESENTATION;
  return (
    <div className="flex flex-wrap gap-2" aria-label="Légende du positionnement marché">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
          badgeClassName,
        )}
        title={description}
      >
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        <span>{label}</span>
      </span>
    </div>
  );
}

/**
 * Maquette #85 point 10 — rappel du Cycle 1 (lecture seule) + ÉCART EN % d'un couple. Rendu SOUS l'éditeur
 * en Cycle 2 (interrupteur « Comparer au cycle 1 » ON). WCAG 2.1 AA : le sens de l'écart est porté par le
 * SIGNE (+ / - / =), jamais la seule couleur (rouge = baisse, renfort). Rien si aucun rappel Cycle 1.
 */
function Cycle1Recall({
  cycle1ValueEur,
  currentValueEur,
}: {
  readonly cycle1ValueEur: number | null;
  readonly currentValueEur: number | null;
}): React.JSX.Element | null {
  if (cycle1ValueEur === null) {
    return null;
  }
  const diff = computeCycle1Diff(cycle1ValueEur, currentValueEur);
  let ecartText: string | null = null;
  let ecartClass = 'text-muted-foreground';
  if (diff !== null) {
    if (diff.direction === 'equal') {
      ecartText = '=';
    } else if (diff.deltaPercent !== null) {
      ecartText = `${diff.deltaPercent > 0 ? '+' : ''}${String(diff.deltaPercent)} %`;
    } else {
      ecartText = `${diff.deltaEur > 0 ? '+' : ''}${String(diff.deltaEur)} €`;
    }
    ecartClass =
      diff.direction === 'down'
        ? 'font-medium text-destructive'
        : diff.direction === 'up'
          ? 'text-foreground'
          : 'text-muted-foreground';
  }
  return (
    <div
      data-testid="cycle1-recall"
      className="flex flex-wrap items-baseline justify-end gap-x-1.5 text-xs text-muted-foreground"
    >
      {/* Maquette #90 : la 2e ligne montre « <valeur cycle 1> € <écart %> », alignée à droite, SANS le
          préfixe « Cycle 1 : » (le contexte est déjà donné par l'interrupteur « Comparer au cycle 1 »).
          Libellé accessible conservé pour lever l'ambiguïté au lecteur d'écran. */}
      <span>
        <span className="sr-only">Cycle 1&nbsp;: </span>
        {String(cycle1ValueEur)}&nbsp;€
      </span>
      {ecartText !== null && (
        <span className={ecartClass}>
          {/* Écart accessible : le SIGNE porte le sens (hausse/baisse/inchangé), doublé de la couleur. */}
          <span className="sr-only">écart&nbsp;</span>
          {ecartText}
        </span>
      )}
    </div>
  );
}
